import axios from "axios";
import { getTokenForDrain } from "../../utils/OfflineDrain";
import {
  DeleteList,
  DeleteListItem,
  DeleteListItems,
  LISTS_API_NOT_CONFIGURED,
  PostList,
  PostListItem,
  isListsApiConfigured,
} from "./ListsCrud";
import {
  PendingListEntry,
  PendingListOp,
  queueCount,
  queueDeleteOp,
  queueGetAll,
  queuePutEntry,
} from "./ListsOfflineQueue";

interface ListsDrainCallbacks {
  onCountChange: (count: number) => void;
  onSynced: (count: number) => void;
}

let callbacks: ListsDrainCallbacks | null = null;
let triggersActive = false;
let drainInterval: ReturnType<typeof setInterval> | null = null;
let draining = false;

const DRAIN_INTERVAL_MS = 45000;

// Which failures mean "try again later" rather than "this will never work". A missing
// response is the slow-network case this exists for. 5xx is the server being briefly
// unhappy. 401 and 599 are the session and the subscription, which retrying cannot fix,
// and an unconfigured API is a deployment state, not an outage.
export function isQueueableError(error: unknown): boolean {
  if (error instanceof Error && error.message === LISTS_API_NOT_CONFIGURED) {
    return false;
  }
  if (!axios.isAxiosError(error)) {
    return false;
  }
  if (error.response == null) {
    return true;
  }
  const status = error.response.status;
  return status >= 500 && status !== 599;
}

// A genuine connectivity failure, as opposed to a server error. Used to abandon the rest
// of the drain rather than hammer a dead connection.
function isNetworkError(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    (error.response == null ||
      error.code === "ECONNABORTED" ||
      error.code === "ERR_NETWORK")
  );
}

function runOp(op: PendingListOp, token: string, dataUserId: string) {
  const tokenGetter = () => Promise.resolve(token);
  switch (op.kind) {
    case "list.upsert":
      return PostList(op.list, tokenGetter, dataUserId);
    case "list.delete":
      return DeleteList(op.listId, tokenGetter, dataUserId);
    case "item.upsert":
      return PostListItem(op.item, tokenGetter, dataUserId);
    case "item.delete":
      return DeleteListItem(op.listId, op.itemId, tokenGetter, dataUserId);
    case "items.delete":
      return DeleteListItems(op.listId, op.itemIds, tokenGetter, dataUserId);
  }
}

export async function drainListsQueue(): Promise<void> {
  if (draining) {
    return; // A trigger fired while the previous pass is still running.
  }
  if (!navigator.onLine || !isListsApiConfigured()) {
    return;
  }
  // Matches the inventory drain's rule. Queued rows carry the user they belong to, and
  // draining mid-impersonation would apply one account's queued work while another
  // account's data is on screen.
  if (localStorage.getItem("impersonatedUserId")) {
    return;
  }

  draining = true;
  try {
    let token: string;
    try {
      token = await getTokenForDrain();
    } catch {
      return; // Refresh token expired; the app's auth flow handles it.
    }

    const entries: PendingListEntry[] = await queueGetAll();
    if (entries.length === 0) {
      return;
    }

    let synced = 0;
    for (const entry of entries) {
      try {
        await runOp(entry.op, token, entry.dataUserId);
        await queueDeleteOp(entry.opId);
        synced++;
      } catch (error) {
        if (isNetworkError(error)) {
          break; // Still offline. Leave the remainder queued.
        }
        entry.attempts++;
        await queuePutEntry(entry);
      }
    }

    const remaining = await queueCount();
    callbacks?.onCountChange(remaining);
    if (synced > 0) {
      callbacks?.onSynced(synced);
    }
  } finally {
    draining = false;
  }
}

function onVisibilityChange(): void {
  if (!document.hidden) {
    drainListsQueue();
  }
}

// Idempotent, and only started once there is actually something queued. A user who never
// loses signal never pays for the interval.
export function ensureListsDrainTriggers(cbs: ListsDrainCallbacks): void {
  callbacks = cbs;
  if (triggersActive) {
    return;
  }
  triggersActive = true;
  window.addEventListener("online", drainListsQueue);
  document.addEventListener("visibilitychange", onVisibilityChange);
  drainInterval = setInterval(drainListsQueue, DRAIN_INTERVAL_MS);
}

export function teardownListsDrainTriggers(): void {
  window.removeEventListener("online", drainListsQueue);
  document.removeEventListener("visibilitychange", onVisibilityChange);
  if (drainInterval != null) {
    clearInterval(drainInterval);
    drainInterval = null;
  }
  triggersActive = false;
  callbacks = null;
}
